import { lazy } from "react";
import { Navigate } from "react-router-dom";

import ErrorBoundary from "../components/ErrorBoundary";
import Loadable from "../components/Loadable";
import ScheduleDetails from "../components/schedule/scheduleDetails";
import WebSocket from "../components/webSocket/webSocket";
import MinimalLayout from "../layout/minimal-layout";
import AuthGuard from "../utils/route-guard/auth-guard";
import FeatureRouteGuard from "../utils/route-guard/feature-route-guard";

import type { RouteObject } from "react-router-dom";

const Emails = Loadable(lazy(() => import("../components/email/EmailClient")));

// Lazy load Phones component
const Phones = Loadable(lazy(() => import("../components/data/phones")));

const VoyagesDashboard = Loadable(lazy(() => import("../views/voyages/index")));
const VoyagesDetails = Loadable(
  lazy(() => import("../components/voyages/vesselDetails"))
);
const Activities = Loadable(
  lazy(() => import("../components/activities/activities"))
);
const Calls = Loadable(lazy(() => import("../components/calls/calls")));
const Pets = Loadable(lazy(() => import("../components/pets/pets")));
const Data = Loadable(lazy(() => import("../components/data/data")));
const Reports = Loadable(lazy(() => import("../components/reports/reports")));
const Settings = Loadable(
  lazy(() => import("../components/settings/settings"))
);
const DeprecatedReports = Loadable(
  lazy(() =>
    import("../components/settings/settings").then((module) => ({
      default: module.DeprecatedReports,
    }))
  )
);
const Voyages = Loadable(lazy(() => import("../components/voyages/voyages")));
const Maps = Loadable(lazy(() => import("../components/map/map")));
const CompanyStats = Loadable(lazy(() => import("../components/stats/companyStats")));
const isFleetMapRouteEnabled =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_FLEET_MAP === "true";
const isCharterAnalyticsRouteEnabled =
  import.meta.env.DEV ||
  import.meta.env.VITE_ENABLE_CHARTER_ANALYTICS === "true";
const Update = isFleetMapRouteEnabled
  ? Loadable(lazy(() => import("../components/update/Update")))
  : null;
const CharterAnalytics = isCharterAnalyticsRouteEnabled
  ? Loadable(lazy(() => import("../components/analytics/CharterAnalytics")))
  : null;
const VoyagesTimeline = Loadable(lazy(() => import("../components/timeline")));

// voyages components
const General = Loadable(lazy(() => import("../components/voyages/general")));
const BerthRotations = Loadable(
  lazy(() => import("../components/voyages/berthRotation"))
);
const Documents = Loadable(
  lazy(() => import("../components/voyages/documents"))
);
const CaptainSofs = Loadable(
  lazy(() => import("../components/voyages/captainSofs"))
);
const PetsList = Loadable(lazy(() => import("../components/voyages/pets")));
const VoyageTasks = Loadable(
  lazy(() => import("../components/voyages/voyageTasks"))
);

// schedule components
const ScheduleGeneral = Loadable(
  lazy(() => import("../components/schedule/general"))
);
const Cargo = Loadable(lazy(() => import("../components/schedule/cargo")));
const OnBoardServices = Loadable(
  lazy(() => import("../components/schedule/onBoardServices"))
);
const Delivery = Loadable(
  lazy(() => import("../components/schedule/delivery"))
);
const BunkerFreshWater = Loadable(
  lazy(() => import("../components/schedule/bunkerFreshWater"))
);
const PetsDetails = Loadable(
  lazy(() => import("../components/pets/petsDetails"))
);

// Data Components
const People = Loadable(lazy(() => import("../components/data/people")));
const Companies = Loadable(lazy(() => import("../components/data/companies")));
const Locations = Loadable(lazy(() => import("../components/data/locations")));
const DirectBilling = Loadable(
  lazy(() => import("../components/data/directBilling"))
);
const PortCallings = Loadable(
  lazy(() => import("../components/data/portCallings"))
);
const Shifting = Loadable(lazy(() => import("../components/data/shiftting")));
const Closures = Loadable(
  lazy(() => import("../components/data/channelClosure"))
);
const PilotCharges = Loadable(
  lazy(() => import("../components/data/pilotCharges"))
);
const AgencyFee = Loadable(lazy(() => import("../components/data/agencyFee")));
const Accounting = Loadable(
  lazy(() => import("../components/data/accounting"))
);
const Vessel = Loadable(lazy(() => import("../components/data/vessel")));
const DockageFee = Loadable(
  lazy(() => import("../components/data/dockageFee"))
);
const Clousers = Loadable(lazy(() => import("../components/reports/closures")));
const PortCallNotification = Loadable(
  lazy(() => import("../components/reports/portCallNotification"))
);
const EmailHistory = Loadable(
  lazy(() => import("../components/reports/emailHistory"))
);
const PDACalculator = Loadable(
  lazy(() => import("../components/reports/pdaCalculator"))
);
const DockRestirctions = Loadable(
  lazy(() => import("../components/reports/dockRestirctions"))
);

// Tasks
const Tasks = Loadable(lazy(() => import("../components/tasks/Tasks")));
const Terminal = Loadable(lazy(() => import("../components/terminal")));
const PilotTracker = Loadable(lazy(() => import("../components/pilotTracker")));
const CaptainDocument = Loadable(
  lazy(() => import("../components/captainSofs/sofDocuments"))
);
const StatementOfFacts = Loadable(
  lazy(() => import("../components/captainSofs/statementOfFact"))
);
const Arrangements = Loadable(
  lazy(() => import("../components/Arrangements/arrangements"))
);
const Crew = Loadable(lazy(() => import("../components/voyages/crew")));

const BlackListComponent = Loadable(
  lazy(() => import("../components/data/callBlackList"))
);
const KnowledgeBaseComponent = Loadable(
  lazy(() => import("../components/data/knowledgeBase"))
);
const CategoriesComponent = Loadable(
  lazy(() => import("../components/data/categories"))
);
const SystemPromptsComponent = Loadable(
  lazy(() => import("../components/data/systemPrompts"))
);
const DocumentTypeComponent = Loadable(
  lazy(() => import("../components/data/documentType"))
);
const GatesComponent = Loadable(lazy(() => import("../components/data/gates")));
const Husbandry = Loadable(lazy(() => import("../components/husbandry/husbandry")));
const GateAccessComponent = Loadable(
  lazy(() => import("../components/data/gateAccess"))
);
const AisVesselsComponent = Loadable(
  lazy(() => import("../components/data/aisVessels"))
);
const GradesComponent = Loadable(
  lazy(() => import("../components/data/grades"))
);

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes: RouteObject[] = [
  {
    path: "/",
    element: (
      <AuthGuard>
        <WebSocket>
          <MinimalLayout />
        </WebSocket>
      </AuthGuard>
    ),
    children: [
      {
        path: "/",
        index: true,
        element: <Navigate to="/voyages" replace />,
      },
      {
        path: "/",
        element: <VoyagesDashboard />, // Main Voyages layout
        children: [
          {
            path: "voyages", // Default view for voyages
            element: <Voyages />,
          },
          { path: "activities", element: <Activities /> },
          { path: "emails", element: <Emails /> },
          {
            path: "reports",
            element: <Navigate to="/reports/port-call-notification" />,
          },
          {
            path: "settings",
            element: <Navigate to="/settings/deprecated/reports" />,
          },
          {
            path: "tasks",
            element: <Tasks />,
          },
          {
            path: "stats",
            element: <CompanyStats />,
          },
          ...(Update
            ? [
                {
                  path: "update",
                  element: (
                    <FeatureRouteGuard feature="fleetMap">
                      <ErrorBoundary>
                        <Update />
                      </ErrorBoundary>
                    </FeatureRouteGuard>
                  ),
                },
              ]
            : []),
          ...(CharterAnalytics
            ? [
                {
                  path: "analytics",
                  element: (
                    <FeatureRouteGuard feature="charterAnalytics">
                      <ErrorBoundary>
                        <CharterAnalytics />
                      </ErrorBoundary>
                    </FeatureRouteGuard>
                  ),
                },
              ]
            : []),
          {
            path: "terminals",
            element: <Terminal />,
          },
          {
            path: "pilot-tracker",
            element: <PilotTracker />,
          },
          {
            path: "reports",
            element: <Reports />,
            children: [
              {
                path: "port-call-notification",
                element: <PortCallNotification />,
              },
              { path: "closures", element: <Clousers /> },
              { path: "email-history", element: <EmailHistory /> },
              { path: "pda-calculator", element: <PDACalculator /> },
              { path: "dock-restrictions", element: <DockRestirctions /> },
            ],
          },
          {
            path: "settings",
            element: <Settings />,
            children: [
              {
                path: "deprecated/reports",
                element: <DeprecatedReports />,
              },
            ],
          },
          { path: "pets", element: <Pets /> },
          { path: "calls", element: <Calls /> },
          { path: "data", element: <Data /> },
          { path: "maps", element: <Maps /> },
          { path: "timeline", element: <VoyagesTimeline /> },
          { path: "husbandry", element: <Husbandry /> },
          { path: "data", element: <Navigate to="/data/locations" /> },
          {
            path: "data",
            element: <Data />,
            children: [
              { path: "people", element: <People /> },
              { path: "phones", element: <Phones /> },
              { path: "companies", element: <Companies /> },
              { path: "locations", element: <Locations /> },
              { path: "direct-billing", element: <DirectBilling /> },
              { path: "port-callings", element: <PortCallings /> },
              { path: "shifting-matrix", element: <Shifting /> },
              { path: "channel-closures", element: <Closures /> },
              { path: "pilot-charges", element: <PilotCharges /> },
              { path: "agency-fee", element: <AgencyFee /> },
              { path: "accounting", element: <Accounting /> },
              { path: "vessel", element: <Vessel /> },
              { path: "dockage-fee", element: <DockageFee /> },
              { path: "blacklist", element: <BlackListComponent /> },
              { path: "knowledge-base", element: <KnowledgeBaseComponent /> },
              { path: "categories", element: <CategoriesComponent /> },
              { path: "system-prompts", element: <SystemPromptsComponent /> },
              { path: "document-types", element: <DocumentTypeComponent /> },
              { path: "gates", element: <GatesComponent /> },
              { path: "gate-access", element: <GateAccessComponent /> },
              { path: "ais-vessels", element: <AisVesselsComponent /> },
              { path: "grades", element: <GradesComponent /> },
            ],
          },
        ],
      },
      {
        path: "/voyages/:id",
        element: <VoyagesDetails />,
        children: [
          {
            path: "general",
            element: <General />,
          },
          {
            path: "berthRotations",
            element: <BerthRotations />,
          },
          {
            path: "arrangements",
            element: <Arrangements />,
          },
          {
            path: "crew",
            element: <Crew />,
          },
          {
            path: "documents",
            element: <Documents />,
          },
          {
            path: "captainSofs",
            element: <CaptainSofs />,
            children: [
              {
                path: "",
                element: <CaptainDocument />,
              },
              {
                path: "statement-of-facts",
                element: <StatementOfFacts />,
              },
            ],
          },
          {
            path: "pets",
            element: <PetsList />,
            children: [
              {
                path: "details", // Match job_id as a dynamic parameter
                element: <PetsDetails />,
              },
            ],
          },
          {
            path: "tasks",
            element: <VoyageTasks />,
          },
          {
            path: "berthrotations/:berthId",
            element: <ScheduleDetails />,
            children: [
              {
                path: "general",
                element: <ScheduleGeneral />,
              },
              {
                path: "cargoes",
                element: <Cargo />,
              },
              {
                path: "deliveries",
                element: <Delivery />,
              },
              {
                path: "onboardservices",
                element: <OnBoardServices />,
              },
              {
                path: "bunkers",
                element: <BunkerFreshWater />,
              },
            ],
          },
        ],
      },
      {
        path: "pets/:pets_id",
        element: <PetsDetails />,
      },
      {
        path: "pets/add-event",
        element: <PetsDetails />,
      },
    ],
  },
];

export default MainRoutes;
